import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Box, Button, IconButton, Typography, GlobalStyles, TextField, MenuItem, Dialog, DialogTitle, DialogContent,
    DialogActions, Divider, List, ListItem, ListItemText, ListSubheader, Paper, Tooltip, Snackbar, Alert, Switch, FormControlLabel
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Close as CloseIcon, Save as SaveIcon } from '@mui/icons-material';
import DCXToolingService from '../../services/DCXToolingService';

const PersonaRoleManager = () => {
    // Theme & effects (match SprocConsole vibe)
    const [darkMode, setDarkMode] = useState(false);
    const [effectsEnabled, setEffectsEnabled] = useState(false);
    // Data
    const [allStorefronts, setAllStorefronts] = useState([]);
    const [allPersonas, setAllPersonas] = useState([]);
    const [allRoles, setAllRoles] = useState([]);
    // Filtering
    const [selectedStorefrontId, setSelectedStorefrontId] = useState('');
    const [filteredPersonas, setFilteredPersonas] = useState([]);
    // Add (Step 1: details)
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [detailsForm, setDetailsForm] = useState({
        name: '',
        displayName: '',
        sortOrder: '',
        storefrontId: '',
    });
    const [detailsErrors, setDetailsErrors] = useState({});

    // Editor (Step 2: transfer list)
    const [isEditorOpen, setIsEditorOpen] = useState(false);
    const [editorMode, setEditorMode] = useState('add'); // 'add' | 'edit'
    const [workingPersona, setWorkingPersona] = useState(null);
    const [editorErrors, setEditorErrors] = useState({});

    // Right list (assigned) and Left list (available)
    const [personaRoleIds, setPersonaRoleIds] = useState([]);     // Role IDs (assigned)
    const [unassignedRoleIds, setUnassignedRoleIds] = useState([]); // Role IDs (available)

    // Delete dialog
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);

    // Feedback
    const [snack, setSnack] = useState({ open: false, severity: 'success', msg: '' });

    // Helper: map backend's returned string to snackbar severity
    const severityFrom = (msg = '') =>
        /success/i.test(msg) ? 'success' : (/fail|error/i.test(msg) ? 'error' : 'info');

    // Load data
    const loadData = async () => {
        const uni = await DCXToolingService.getUniversalParamaterData();
        setAllStorefronts(uni.storefronts || []);

        const { allPersonas: personas = [], allRoles: roles = [] } =
            await DCXToolingService.getAllPersonasAndRoles();

        setAllPersonas(personas);
        setAllRoles(roles);
    };

    useEffect(() => { loadData(); }, []);

    useEffect(() => {
        const list = (allPersonas || [])
            .filter(p => !selectedStorefrontId || String(p.storefrontId) === String(selectedStorefrontId))
            .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
        setFilteredPersonas(list);
    }, [allPersonas, selectedStorefrontId]);

    const roleLookup = useMemo(() => {
        const m = new Map();
        (allRoles ?? []).forEach(r => {
            const key = String(r?.id);
            if (key) {
                m.set(key, {
                    id: key,
                    name: r.name,
                    displayName: r.displayName,
                    code: r.code
                });
            }
        });
        if (workingPersona?.personaRoles?.length) {
            workingPersona.personaRoles.forEach(pr => {
                const rr = pr?.role;
                const key = rr?.id != null ? String(rr.id) : '';
                if (key && !m.has(key)) {
                    m.set(key, {
                        id: key,
                        name: rr.name,
                        displayName: rr.displayName
                    });
                }
            });
        }
        return m;
    }, [allRoles, workingPersona]);

    const getUsedSortOrders = (storefrontId, excludePersonaId = null) => {
        const sfIdStr = String(storefrontId);
        const s = new Set(
            (allPersonas || [])
                .filter(p =>
                    p.storefrontId != null &&
                    String(p.storefrontId) === sfIdStr &&
                    (excludePersonaId == null || String(p.id) !== String(excludePersonaId))
                )
                .map(p => Number(p.sortOrder))
                .filter(n => !Number.isNaN(n) && n >= 1)
        );
        return s;
    };

    const computeLowestAvailableSortOrder = (storefrontId) => {
        if (!storefrontId) return '';
        const used = getUsedSortOrders(storefrontId);
        let i = 1;
        while (used.has(i)) i++;
        return i;
    };

    const openAddFlow = () => {
        setDetailsErrors({});
        setDetailsForm({
            name: '',
            displayName: '',
            sortOrder: selectedStorefrontId ? computeLowestAvailableSortOrder(selectedStorefrontId) : '',
            storefrontId: selectedStorefrontId || '',
        });
        setIsDetailsModalOpen(true);
    };

    const onDetailsStorefrontChange = (newSfId) => {
        const next = computeLowestAvailableSortOrder(newSfId);
        setDetailsForm(f => ({
            ...f,
            storefrontId: newSfId,
            sortOrder: next || ''
        }));
        setDetailsErrors(err => ({ ...err, storefrontId: undefined, sortOrder: undefined }));
    };

    const validateDetails = () => {
        const e = {};
        if (!detailsForm.name?.trim()) e.name = 'Required';
        if (!detailsForm.displayName?.trim()) e.displayName = 'Required';
        if (!detailsForm.storefrontId) e.storefrontId = 'Required';

        const so = Number(detailsForm.sortOrder);
        if (detailsForm.sortOrder === '' || Number.isNaN(so) || so < 1) {
            e.sortOrder = 'Positive integer required';
        } else {
            const used = getUsedSortOrders(detailsForm.storefrontId);
            if (used.has(so)) e.sortOrder = 'Already in use for this storefront';
        }

        setDetailsErrors(e);
        return Object.keys(e).length === 0;
    };

    const continueToTransferList = () => {
        if (!validateDetails()) return;

        const tempPersona = {
            id: null,
            name: detailsForm.name.trim(),
            displayName: detailsForm.displayName.trim(),
            sortOrder: Number(detailsForm.sortOrder),
            storefrontId: detailsForm.storefrontId,
            personaRoles: []
        };

        setWorkingPersona(tempPersona);

        const allKeys = (allRoles ?? []).map(r => String(r.id)).filter(Boolean);
        setPersonaRoleIds([]);
        setUnassignedRoleIds(allKeys);

        setEditorMode('add');
        setIsDetailsModalOpen(false);
        setIsEditorOpen(true);
        setEditorErrors({});
    };

    const openEdit = (persona) => {
        const assigned = (persona.personaRoles ?? [])
            .map(pr => pr?.role?.id)
            .filter((id) => id !== null && id !== undefined)
            .map(String);

        const assignedSet = new Set(assigned);

        setWorkingPersona({
            id: persona.id,
            name: persona.name ?? '',
            displayName: persona.displayName ?? '',
            sortOrder: persona.sortOrder,
            storefrontId: persona.storefrontId,
            personaRoles: Array.isArray(persona.personaRoles) ? [...persona.personaRoles] : []
        });

        setPersonaRoleIds(assigned);
        setUnassignedRoleIds(
            (allRoles ?? [])
                .map(r => String(r.id))
                .filter((rid) => rid && !assignedSet.has(rid))
        );

        setEditorMode('edit');
        setIsEditorOpen(true);
        setEditorErrors({});
    };

    const openDelete = (persona) => {
        setDeleteTarget(persona);
        setDeleteConfirmText('');
        setIsDeleteOpen(true);
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        try {
            const result = await DCXToolingService.deletePersona(deleteTarget.id);
            setSnack({ open: true, severity: severityFrom(result), msg: result });
            setIsDeleteOpen(false);
            setDeleteTarget(null);
            await loadData();
        } catch (err) {
            setSnack({ open: true, severity: 'error', msg: err?.message || 'Delete failed' });
        }
    };

    const dragStateRef = useRef({ sourceList: null, roleId: null, fromIndex: -1 });

    const onDragStart = (listKey, roleId, index) => (e) => {
        dragStateRef.current = { sourceList: listKey, roleId, fromIndex: index };
        e.dataTransfer.setData('text/plain', `${listKey}\n${roleId}\n${index}`);
        e.dataTransfer.effectAllowed = 'move';
    };

    const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };

    const onDropToList = (targetListKey, targetIndex = -1) => (e) => {
        e.preventDefault();
        const dt = e.dataTransfer.getData('text/plain');
        let sourceList, roleIdStr, fromIndexStr;
        if (dt) {
            [sourceList, roleIdStr, fromIndexStr] = dt.split('\n');
        } else {
            ({ sourceList, roleId: roleIdStr, fromIndex: fromIndexStr } = dragStateRef.current || {});
        }
        const roleId = Number(roleIdStr);
        const fromIndex = Number(fromIndexStr);

        if (!roleId || !sourceList) return;
        if (sourceList === targetListKey && fromIndex === targetIndex) return;

        const move = (fromArr, toArr) => {
            const idx = fromArr.indexOf(roleId);
            if (idx >= 0) {
                const copyFrom = [...fromArr];
                const [moved] = copyFrom.splice(idx, 1);
                const copyTo = [...toArr];
                const insertAt = targetIndex >= 0 ? targetIndex : copyTo.length;
                copyTo.splice(insertAt, 0, moved);
                return { fromOut: copyFrom, toOut: copyTo };
            }
            return null;
        };

        if (sourceList === 'unassigned' && targetListKey === 'assigned') {
            const res = move(unassignedRoleIds, personaRoleIds);
            if (res) { setUnassignedRoleIds(res.fromOut); setPersonaRoleIds(res.toOut); }
        } else if (sourceList === 'assigned' && targetListKey === 'unassigned') {
            const res = move(personaRoleIds, unassignedRoleIds);
            if (res) { setPersonaRoleIds(res.fromOut); setUnassignedRoleIds(res.toOut); }
        } else if (sourceList === 'assigned' && targetListKey === 'assigned') {
            const copy = [...personaRoleIds];
            const idx = copy.indexOf(roleId);
            if (idx >= 0) {
                copy.splice(idx, 1);
                const insertAt = targetIndex >= 0 ? targetIndex : copy.length;
                copy.splice(insertAt, 0, roleId);
                setPersonaRoleIds(copy);
            }
        } else if (sourceList === 'unassigned' && targetListKey === 'unassigned') {
            const copy = [...unassignedRoleIds];
            const idx = copy.indexOf(roleId);
            if (idx >= 0) {
                copy.splice(idx, 1);
                const insertAt = targetIndex >= 0 ? targetIndex : copy.length;
                copy.splice(insertAt, 0, roleId);
                setUnassignedRoleIds(copy);
            }
        }
    };

    const moveSelected = (roleId, toAssigned) => {
        if (toAssigned) {
            if (!personaRoleIds.includes(roleId)) {
                setPersonaRoleIds(prev => [...prev, roleId]);
                setUnassignedRoleIds(prev => prev.filter(id => id !== roleId));
            }
        } else {
            if (!unassignedRoleIds.includes(roleId)) {
                setUnassignedRoleIds(prev => [...prev, roleId]);
                setPersonaRoleIds(prev => prev.filter(id => id !== roleId));
            }
        }
    };

    const validateEditor = () => {
        const e = {};
        if (!workingPersona?.name?.trim()) e.name = 'Required';
        if (!workingPersona?.displayName?.trim()) e.displayName = 'Required';
        if (!workingPersona?.storefrontId) e.storefrontId = 'Required';

        const so = Number(workingPersona?.sortOrder);
        if (workingPersona?.sortOrder === '' || Number.isNaN(so) || so < 1) {
            e.sortOrder = 'Positive integer required';
        } else {
            const used = getUsedSortOrders(workingPersona.storefrontId, workingPersona.id ?? null);
            if (used.has(so)) e.sortOrder = 'Already in use for this storefront';
        }

        setEditorErrors(e);
        return Object.keys(e).length === 0;
    };

    const persistPersona = async () => {
        if (!workingPersona) return;
        if (!validateEditor()) return;

        
        const payload = {
            id: workingPersona.id ?? null,
            name: workingPersona.name.trim(),
            displayName: workingPersona.displayName.trim(),
            sortOrder: Number(workingPersona.sortOrder),
            storefrontId: workingPersona.storefrontId,
            personaRoles: personaRoleIds
        };

        try {
            if (editorMode === 'add') {
                const result = await DCXToolingService.addPersonaRoles(payload);
                setSnack({ open: true, severity: severityFrom(result), msg: result });
            } else {
                const result = await DCXToolingService.editPersonaRoles(payload);
                setSnack({ open: true, severity: severityFrom(result), msg: result });
            }
            setIsEditorOpen(false);
            setWorkingPersona(null);
            await loadData();
        } catch (err) {
            setSnack({ open: true, severity: 'error', msg: err?.message || 'Save failed' });
        }
    };

    const NeonHeader = (
        <Typography
            variant="h5"
            gutterBottom
            sx={{
                color: darkMode ? '#ff00ff' : '#333',
                textShadow: effectsEnabled ? '0 0 5px #ff00ff' : '',
                mb: 2
            }}
        >
            ⚡ Persona Role Manager
        </Typography>
    );

    const StorefrontSelector = (
        <TextField
            select
            label="Storefront"
            value={selectedStorefrontId}
            onChange={(e) => setSelectedStorefrontId(e.target.value)}
            sx={{
                width: 320,
                '& .MuiOutlinedInput-root': {
                    height: 40,
                    border: 'none',
                    backgroundColor: darkMode ? '#1a1a1a' : '#fff',
                    '& fieldset': { borderColor: darkMode ? '#ff00ff' : '#ccc' },
                    '&:hover fieldset': { borderColor: darkMode ? '#00ffff' : '#999' },
                    '&.Mui-focused fieldset': { borderColor: darkMode ? '#00ffff' : '#666' },
                },
                label: { color: darkMode ? '#ff00ff' : '#333' },
                '& .MuiSelect-select': { pt: '10px', pb: '10px', color: darkMode ? '#ff00ff' : '#333' },
            }}
        >
            <MenuItem value=""><em>All Storefronts</em></MenuItem>
            {allStorefronts.map(sf => (
                <MenuItem key={sf.id} value={sf.id}>{sf.displayName}</MenuItem>
            ))}
        </TextField>
    );

    const roleCountLabel = (persona) => `${(persona.personaRoles?.length ?? 0)} role(s)`;

    const PersonaRow = ({ persona, index }) => {
        return (
            <ListItem
                key={persona.id ?? `p-${index}`}
                sx={{
                    background: darkMode ? '#1a1a1a' : '#fff',
                    border: '1px solid',
                    borderColor: darkMode ? '#ff00ff' : '#ccc',
                    mb: 1,
                    borderRadius: 1
                }}
                secondaryAction={
                    <Box>
                        <Tooltip title="Edit">
                            <IconButton onClick={() => openEdit(persona)} size="small" sx={{ color: darkMode ? '#00ffff' : '#118ABD' }}>
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                            <IconButton
                                onClick={() => openDelete(persona)}
                                size="small"
                                sx={{ color: darkMode ? '#ff4d6a' : '#b00020' }}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                }
            >
                <ListItemText
                    primary={
                        <Box display="flex" gap={2} alignItems="baseline">
                            <Typography sx={{ color: darkMode ? '#00ffff' : '#333' }}>
                                {persona.displayName}
                            </Typography>
                            <Typography variant="caption" sx={{ color: darkMode ? '#ff00ff' : '#666' }}>
                                name: {persona.name || '-'} • order: {persona.sortOrder}
                            </Typography>
                        </Box>
                    }
                    secondary={darkMode ? <span style={{ color: '#aaa' }}>{roleCountLabel(persona)}</span> : roleCountLabel(persona)}
                />
            </ListItem>
        );
    };

    const RoleList = ({ title, roleIds, listKey }) => (
        <Paper
            elevation={0}
            onDragOver={onDragOver}
            onDrop={onDropToList(listKey)}
            sx={{
                flex: 1,
                minHeight: 360,
                border: '1px solid',
                borderColor: darkMode ? '#ff00ff' : '#ccc',
                backgroundColor: darkMode ? '#0e0e0e' : '#fafafa',
                overflow: 'auto'
            }}
        >
            <List
                dense
                subheader={
                    <ListSubheader
                        sx={{
                            position: 'sticky', top: 0, zIndex: 1,
                            background: darkMode ? '#1a1a1a' : '#fff',
                            color: darkMode ? '#00ffff' : '#333',
                            borderBottom: '1px solid',
                            borderColor: darkMode ? '#ff00ff' : '#eee'
                        }}
                    >
                        {title} ({roleIds.length})
                    </ListSubheader>
                }
            >
                {roleIds.map((rid, idx) => {
                    const role = roleLookup.get(String(rid)); // ensure string key
                    if (!role) return null;
                    const label = role.displayName || role.name || `Role ${rid}`;
                    return (
                        <ListItem
                            key={`${listKey}-${rid}`}
                            divider
                            draggable
                            onDragStart={onDragStart(listKey, rid, idx)}
                            onDrop={onDropToList(listKey, idx)}
                            sx={{
                                cursor: 'grab',
                                '&:active': { cursor: 'grabbing' },
                                background: darkMode ? '#141414' : '#fff'
                            }}
                            secondaryAction={
                                <Box>
                                    {listKey === 'unassigned' ? (
                                        <Button size="small" onClick={() => moveSelected(rid, true)} sx={{ color: darkMode ? '#00ffff' : '#118ABD' }}>
                                            Add →
                                        </Button>
                                    ) : (
                                        <Button size="small" onClick={() => moveSelected(rid, false)} sx={{ color: darkMode ? '#ff00ff' : '#b00020' }}>
                                            ← Remove
                                        </Button>
                                    )}
                                </Box>
                            }
                        >
                            <ListItemText
                                primary={<span style={{ color: darkMode ? '#00ffff' : '#333' }}>{label}</span>}
                                secondary={role.name && role.displayName && role.name !== role.displayName
                                    ? <span style={{ color: darkMode ? '#b3b3b3' : '#666' }}>{role.name}</span>
                                    : null}
                            />
                        </ListItem>
                    );
                })}
            </List>
        </Paper>
    );

    return (
        <Box
            p={3}
            sx={{
                backgroundColor: darkMode ? '#0d0d0d' : '#f5f5f5',
                color: darkMode ? '#00ffff' : '#333',
                fontFamily: 'Courier New, monospace',
                minHeight: '100vh',
                transition: 'all 0.3s ease',
            }}
        >
            <GlobalStyles styles={{
                '@keyframes glowPulse': {
                    '0%': { boxShadow: '0 0 5px #ff00ff' },
                    '50%': { boxShadow: '0 0 20px #ff00ff' },
                    '100%': { boxShadow: '0 0 5px #ff00ff' }
                },
                '@keyframes fadeIn': { '0%': { opacity: 0 }, '100%': { opacity: 1 } },
                '@keyframes flicker': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.6 } }
            }} />

            <Box display="flex" justifyContent="space-between" alignItems="center">
                {NeonHeader}

                <Box display="flex" gap={2} alignItems="center">
                    <FormControlLabel
                        control={<Switch checked={darkMode} onChange={() => setDarkMode(!darkMode)} />}
                        label="Dark Mode"
                        sx={{ color: darkMode ? '#00ffff' : '#333' }}
                    />
                    <FormControlLabel
                        control={<Switch checked={effectsEnabled} onChange={() => setEffectsEnabled(!effectsEnabled)} />}
                        label="Effects"
                        sx={{ color: darkMode ? '#00ffff' : '#333' }}
                    />
                </Box>
            </Box>

            <Box display="flex" gap={2} alignItems="center" mb={2}>
                {StorefrontSelector}
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={openAddFlow}
                    sx={{
                        backgroundColor: darkMode ? '#ff00ff' : '#118ABD',
                        color: darkMode ? 'white' : 'black',
                        textTransform: 'none',
                        boxShadow: effectsEnabled ? '0 0 10px #ff00ff' : '',
                        animation: effectsEnabled ? 'glowPulse 2s infinite' : '',
                        '&:hover': { backgroundColor: '#00ffff', boxShadow: '0 0 10px #00ffff', color: '#0d0d0d' }
                    }}
                >
                    Add New Persona
                </Button>
            </Box>

            <Paper
                elevation={0}
                sx={{
                    p: 2,
                    background: darkMode ? '#1a1a1a' : '#fff',
                    border: '1px solid',
                    borderColor: darkMode ? '#ff00ff' : '#ccc'
                }}
            >
                <Typography variant="subtitle1" sx={{ color: darkMode ? '#ff00ff' : '#333', mb: 1 }}>
                    Existing Personas ({filteredPersonas.length})
                </Typography>
                <List dense>
                    {filteredPersonas.map((p, i) => <PersonaRow persona={p} index={i} key={p.id ?? i} />)}
                    {filteredPersonas.length === 0 && (
                        <Typography sx={{ color: darkMode ? '#b3b3b3' : '#666', fontStyle: 'italic' }}>
                            No personas for the selected storefront.
                        </Typography>
                    )}
                </List>
            </Paper>
            <Dialog open={isDetailsModalOpen} onClose={() => setIsDetailsModalOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>New Persona — Details</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Enter required details to continue to role assignment.
                    </Typography>

                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                        <TextField
                            label="Name *"
                            value={detailsForm.name}
                            onChange={(e) => setDetailsForm({ ...detailsForm, name: e.target.value })}
                            error={!!detailsErrors.name}
                            helperText={detailsErrors.name}
                            fullWidth
                        />
                        <TextField
                            label="Display Name *"
                            value={detailsForm.displayName}
                            onChange={(e) => setDetailsForm({ ...detailsForm, displayName: e.target.value })}
                            error={!!detailsErrors.displayName}
                            helperText={detailsErrors.displayName}
                            fullWidth
                        />
                        <TextField
                            label="Sort Order *"
                            value={detailsForm.sortOrder}
                            onChange={(e) => setDetailsForm({ ...detailsForm, sortOrder: e.target.value })}
                            error={!!detailsErrors.sortOrder}
                            helperText={detailsErrors.sortOrder}
                            fullWidth
                        />
                        <TextField
                            select
                            label="Storefront *"
                            value={detailsForm.storefrontId}
                            onChange={(e) => onDetailsStorefrontChange(e.target.value)}
                            error={!!detailsErrors.storefrontId}
                            helperText={detailsErrors.storefrontId}
                            fullWidth
                        >
                            {allStorefronts.map(sf => (
                                <MenuItem key={sf.id} value={sf.id}>{sf.displayName}</MenuItem>
                            ))}
                        </TextField>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsDetailsModalOpen(false)} startIcon={<CloseIcon />}>Cancel</Button>
                    <Button variant="contained" onClick={continueToTransferList} startIcon={<SaveIcon />}>Continue</Button>
                </DialogActions>
            </Dialog>
            <Dialog open={isEditorOpen} onClose={() => setIsEditorOpen(false)} fullWidth maxWidth="lg">
                <DialogTitle>
                    {editorMode === 'add' ? 'Add Persona — Assign Roles' : `Edit Persona — ${workingPersona?.displayName || ''}`}
                </DialogTitle>
                <DialogContent dividers>
                    {workingPersona && (
                        <>
                            <Box display="grid" gridTemplateColumns="repeat(4, 1fr)" gap={2} mb={2}>
                                <TextField
                                    label="Name *"
                                    value={workingPersona.name}
                                    onChange={(e) => setWorkingPersona({ ...workingPersona, name: e.target.value })}
                                    error={!!editorErrors.name}
                                    helperText={editorErrors.name}
                                />
                                <TextField
                                    label="Display Name *"
                                    value={workingPersona.displayName}
                                    onChange={(e) => setWorkingPersona({ ...workingPersona, displayName: e.target.value })}
                                    error={!!editorErrors.displayName}
                                    helperText={editorErrors.displayName}
                                />
                                <TextField
                                    label="Sort Order *"
                                    value={workingPersona.sortOrder}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        setWorkingPersona({ ...workingPersona, sortOrder: v });
                                        setEditorErrors(err => ({ ...err, sortOrder: undefined }));
                                    }}
                                    error={!!editorErrors.sortOrder}
                                    helperText={editorErrors.sortOrder}
                                />
                                <TextField
                                    select
                                    label="Storefront *"
                                    value={workingPersona.storefrontId}
                                    onChange={(e) => {
                                        const newSf = e.target.value;
                                        const so = Number(workingPersona.sortOrder);
                                        let nextSO = workingPersona.sortOrder;
                                        if (!so || Number.isNaN(so) || so < 1 || getUsedSortOrders(newSf, workingPersona.id).has(so)) {
                                            nextSO = computeLowestAvailableSortOrder(newSf);
                                        }
                                        setWorkingPersona({ ...workingPersona, storefrontId: newSf, sortOrder: nextSO });
                                        setEditorErrors(err => ({ ...err, storefrontId: undefined, sortOrder: undefined }));
                                    }}
                                    error={!!editorErrors.storefrontId}
                                    helperText={editorErrors.storefrontId}
                                >
                                    {allStorefronts.map(sf => (
                                        <MenuItem key={sf.id} value={sf.id}>{sf.displayName}</MenuItem>
                                    ))}
                                </TextField>
                            </Box>

                            <Divider sx={{ mb: 2 }} />

                            <Box display="flex" gap={2}>
                                <RoleList title="All Roles" roleIds={unassignedRoleIds} listKey="unassigned" />
                                <RoleList title="Persona Roles" roleIds={personaRoleIds} listKey="assigned" />
                            </Box>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsEditorOpen(false)} startIcon={<CloseIcon />}>Close</Button>
                    <Button
                        variant="contained"
                        onClick={persistPersona}
                        startIcon={<SaveIcon />}
                        sx={{
                            backgroundColor: darkMode ? '#ff00ff' : '#118ABD',
                            color: darkMode ? 'white' : 'black',
                            '&:hover': { backgroundColor: '#00ffff', color: '#0d0d0d' }
                        }}
                    >
                        Save Changes
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={isDeleteOpen} onClose={() => setIsDeleteOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Delete Persona</DialogTitle>
                <DialogContent dividers>
                    <Typography sx={{ mb: 2 }}>
                        Type <strong>{deleteTarget?.displayName}</strong> to confirm deletion.
                    </Typography>
                    <TextField
                        fullWidth
                        label="Confirm Display Name"
                        value={deleteConfirmText}
                        onChange={(e) => setDeleteConfirmText(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsDeleteOpen(false)} startIcon={<CloseIcon />}>Cancel</Button>
                    <Button
                        color="error"
                        variant="contained"
                        startIcon={<DeleteIcon />}
                        onClick={confirmDelete}
                        disabled={!deleteTarget || deleteConfirmText !== deleteTarget.displayName}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snack.open}
                autoHideDuration={3500}
                onClose={() => setSnack({ ...snack, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snack.severity} variant="filled" onClose={() => setSnack({ ...snack, open: false })}>
                    {snack.msg}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default PersonaRoleManager;